<script setup lang="ts">
import { computed } from 'vue'

import { useInsightActionCommands } from '../composables/useInsightActionCommands'
import { useStepFooterCopy } from '../composables/useStepFooterCopy'
import { useInsightActionStore } from '../stores/insightActionStore'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'

const { t } = useI18n()
const store = useInsightActionStore()
const commands = useInsightActionCommands()
const copy = useStepFooterCopy()

const shadow = computed(() => store.executionEnabled === false)
</script>

<template>
  <div class="insight-action-step-footer flex w-full items-center justify-between gap-md">
    <div class="min-w-0" aria-live="polite">
      <div class="text-xs font-semibold">{{ copy.headline.value }}</div>
      <div v-if="copy.coverage.value" class="text-2xs text-muted">
        {{ copy.coverage.value }}
      </div>
      <div v-if="copy.note.value" class="text-2xs text-muted" data-testid="step-note">
        {{ copy.note.value }}
      </div>
    </div>

    <div class="flex items-center gap-xs">
      <Button
        v-if="store.mode === 'history'"
        variant="soft"
        size="sm"
        data-testid="history-back"
        @click="store.setMode('read')"
      >
        {{ t('insightActions.step.backToInsight') }}
      </Button>

      <template v-else-if="store.step === 'select'">
        <Button variant="soft" size="sm" data-testid="step-cancel" @click="store.setMode('read')">
          {{ t('insightActions.step.cancel') }}
        </Button>
        <Button
          v-if="!shadow"
          variant="primary"
          size="sm"
          :disabled="!store.selectedCount"
          data-testid="step-continue"
          @click="commands.enterConfirm()"
        >
          {{ t('insightActions.step.continue') }}
        </Button>
      </template>

      <template v-else-if="store.step === 'confirm'">
        <Button variant="soft" size="sm" data-testid="step-back" @click="store.setStep('select')">
          {{ t('insightActions.step.back') }}
        </Button>
        <Button
          variant="primary"
          size="sm"
          :loading="store.applying"
          :disabled="store.applying || !store.selectedCount"
          data-testid="step-apply"
          @click="commands.apply()"
        >
          {{
            store.applying
              ? t('insightActions.step.sending')
              : t('insightActions.step.apply', { count: store.selectedCount })
          }}
        </Button>
      </template>

      <Button
        v-else-if="store.step === 'progress'"
        variant="soft"
        size="sm"
        data-testid="step-close"
        @click="store.setMode('read')"
      >
        {{ t('insightActions.step.close') }}
      </Button>

      <template v-else>
        <Button
          variant="soft"
          size="sm"
          data-testid="step-history"
          @click="store.setMode('history')"
        >
          {{ t('insightActions.step.viewHistory') }}
        </Button>
        <Button variant="primary" size="sm" data-testid="step-done" @click="store.setMode('read')">
          {{ t('insightActions.step.done') }}
        </Button>
      </template>
    </div>
  </div>
</template>
